import styled from "@emotion/styled"
import {
	PTable,
	PTableBody,
	PTableCell,
	PTableHead,
	PTableHeadCell,
	PTableHeadRow,
	PTableRow,
} from "@porsche-design-system/components-react"
import type { PTableProps } from "@porsche-design-system/components-react"
import type { ReactNode, JSX } from "react"
import type { MessageDescriptor } from "react-intl"
import { FormattedMessage, useIntl } from "react-intl"
import { SkeletonText } from "./SkeletonText"

const times = <T,>(count: number, callback: (index: number) => T): T[] =>
	Array.from({ length: count }, (_, index) => callback(index))

interface SkeletonTableRowProps {
	columns: number
}

const SkeletonTableRow = ({ columns }: SkeletonTableRowProps) => (
	<PTableRow>
		{times(columns, (index) => (
			<PTableCell key={index}>
				<StyledSkeletonText />
			</PTableCell>
		))}
	</PTableRow>
)

interface SkeletonTableProps extends Pick<PTableProps, "compact" | "layout"> {
	caption?: MessageDescriptor | string
	columns?: number
	headings?: (MessageDescriptor | undefined)[] // undefined for empty column head
	renderCustomRow?: (index: number) => JSX.Element
	rows: number
	tableHead?: ReactNode
}

// function overloading to either specify
// - jsx tableHead with the amount of columns or
// - jsx tableHead with renderCustomRow or
// - headings as an array of MessageDescriptors
export function SkeletonTable(
	props: Omit<SkeletonTableProps, "headings"> & Required<Pick<SkeletonTableProps, "columns" | "tableHead">>,
): JSX.Element
export function SkeletonTable(
	props: Omit<SkeletonTableProps, "headings" | "columns"> &
		Required<Pick<SkeletonTableProps, "renderCustomRow" | "tableHead">>,
): JSX.Element
export function SkeletonTable(
	props: Omit<SkeletonTableProps, "columns" | "tableHead"> & Required<Pick<SkeletonTableProps, "headings">>,
): JSX.Element
export function SkeletonTable({
	caption,
	columns,
	headings,
	renderCustomRow,
	rows,
	tableHead,
	...rest
}: SkeletonTableProps) {
	const { formatMessage } = useIntl()

	return (
		<PTable caption={typeof caption === "object" ? formatMessage(caption) : caption} {...rest}>
			{tableHead || (
				<PTableHead>
					<PTableHeadRow>
						{/* if there is no tableHead, headings are required and this fails on purpose */}
						{headings!.map((heading, index) => (
							<PTableHeadCell key={index}>{heading && <FormattedMessage {...heading} />}</PTableHeadCell>
						))}
					</PTableHeadRow>
				</PTableHead>
			)}
			<StyledTableBody>
				{times(
					rows,
					renderCustomRow ??
						((index) => (
							// if there are no columns, headings are required and this fails on purpose
							<SkeletonTableRow key={index} columns={columns ?? headings!.length} />
						)),
				)}
			</StyledTableBody>
		</PTable>
	)
}

const StyledTableBody = styled(PTableBody)`
	> * {
		--_p-table-b: none; /* hover effect is pointless on skeleton */
	}
`

const StyledSkeletonText = styled(SkeletonText)`
	width: 50%;
`
